import { Notebook, Bell, Archive, Trash2, Tag, Pencil, Download, LogIn, LogOut, Eraser, Mail, Plus, Send, UserX, UploadCloud } from "lucide-react";
import { useEffect, useState } from "react";
import { fetchActivity } from "@/lib/transfers";
import { labelStyle } from "@/lib/labelColors";
import ShareAppDialog from "@/components/ShareAppDialog";
import { TipSummaryCard } from "@/components/TipSummaryCard";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/i18n/LanguageContext";
import { exportAllNotes } from "@/lib/exportMarkdown";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import EmailAuthDialog from "@/components/EmailAuthDialog";

const BASE_ITEMS = [
  { key: "notes", labelKey: "nav.notes", icon: Notebook },
  { key: "reminders", labelKey: "nav.reminders", icon: Bell },
  { key: "archive", labelKey: "nav.archive", icon: Archive },
  { key: "trash", labelKey: "nav.trash", icon: Trash2 },
];

export default function Sidebar({ view, setView, labels, onManageLabels, open, onClose, onDropNote }) {
  const { t } = useT();
  const { user, authState, signIn, signOut } = useAuth();
  const [emailAuthOpen, setEmailAuthOpen] = useState(false);
  const [dragOver, setDragOver] = useState(null); // key of the item currently being hovered
  const [newDownloads, setNewDownloads] = useState(0);
  const [shareAppOpen, setShareAppOpen] = useState(false);
  useEffect(() => {
    fetchActivity().then((a) => setNewDownloads(Object.values(a).reduce((n, x) => n + x.unseen, 0))).catch(() => {});
  }, []);
  const isLabelView = view.startsWith("label:");
  const activeLabel = isLabelView ? view.slice(6) : null;
  // Collapse the mobile overlay (if any) before firing the manage-labels
  // callback so the dialog doesn't open hidden behind the slide-in panel.
  const manageLabels = () => { onClose?.(); onManageLabels?.(); };

  // --- Drag-and-drop helpers (desktop only — touch devices never fire
  //     dragstart on an HTML5 draggable, so this is cost-free on mobile).
  const noteIdFromEvent = (e) => {
    // Firefox gives us the id on drop only, Chrome also exposes it during
    // dragover. Fall back to the body-level marker set by NoteCard.
    try {
      const t = e.dataTransfer?.getData?.("application/x-mango-note-id");
      if (t) return t;
    } catch {}
    return document.body?.dataset?.mangoDragging || null;
  };
  const dropHandlers = (key, apply) => ({
    onDragEnter: (e) => {
      if (!noteIdFromEvent(e)) return;
      e.preventDefault(); setDragOver(key);
    },
    onDragOver: (e) => {
      if (!noteIdFromEvent(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragOver !== key) setDragOver(key);
    },
    onDragLeave: () => setDragOver((cur) => (cur === key ? null : cur)),
    onDrop: (e) => {
      e.preventDefault();
      const id = noteIdFromEvent(e);
      setDragOver(null);
      if (id && onDropNote) onDropNote(id, apply);
    },
  });
  const dragClass = (key) => dragOver === key && "ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-950/40 scale-[1.02]";

  const Content = (
    <aside
      className="h-full w-full md:w-64 shrink-0 border-r border-border bg-background/80 backdrop-blur-sm py-4 px-2 flex flex-col"
      data-testid="sidebar"
    >
      <nav className="flex-1 space-y-1 overflow-y-auto">
        {BASE_ITEMS.map(({ key, labelKey, icon: Icon }) => {
          const active = view === key;
          // Only archive/trash/reminders accept drops. Each applies its
          // own action to the dropped note (archive => flip archived,
          // trash => flip trashed, reminders => just switch view).
          const dropper = key === "archive"
            ? dropHandlers("archive", (noteId) => onDropNote?.(noteId, "archive"))
            : key === "trash"
            ? dropHandlers("trash", (noteId) => onDropNote?.(noteId, "trash"))
            : key === "reminders"
            ? dropHandlers("reminders", () => { setView("reminders"); onClose?.(); })
            : {};
          const isDropTarget = key === "archive" || key === "trash" || key === "reminders";
          return (
            <button
              key={key}
              onClick={() => { setView(key); onClose?.(); }}
              data-testid={`sidebar-${key}-link`}
              {...(isDropTarget ? {
                onDragEnter: dropper.onDragEnter,
                onDragOver: dropper.onDragOver,
                onDragLeave: dropper.onDragLeave,
                onDrop: (e) => {
                  e.preventDefault();
                  const id = noteIdFromEvent(e);
                  setDragOver(null);
                  if (!id) return;
                  if (key === "archive") onDropNote?.(id, "archive");
                  else if (key === "trash") onDropNote?.(id, "trash");
                  else if (key === "reminders") { setView("reminders"); onClose?.(); }
                },
              } : {})}
              className={cn(
                "group w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-sm font-medium transition-all",
                "hover:bg-stone-100 dark:hover:bg-stone-800",
                active && "bg-stone-900 text-stone-50 dark:bg-stone-100 dark:text-stone-900 hover:bg-stone-900 dark:hover:bg-stone-100",
                dragClass(key)
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{t(labelKey)}</span>
            </button>
          );
        })}

        <div className="pt-5 pb-2 px-4 flex items-center justify-between">
          <button
            onClick={manageLabels}
            className="font-mono-xs text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors"
            data-testid="manage-labels-heading"
          >
            {t("nav.labels")}
          </button>
          <button
            onClick={manageLabels}
            data-testid="manage-labels-btn"
            className="h-7 px-2 inline-flex items-center gap-1 rounded-full text-xs font-medium text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
            aria-label="Create or edit labels"
            title="Create or edit labels"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>New</span>
          </button>
        </div>

        {labels.length === 0 ? (
          <button
            onClick={manageLabels}
            className="w-full text-left px-4 text-xs text-stone-500 dark:text-stone-500 italic hover:text-amber-700 dark:hover:text-amber-300 transition-colors py-1"
            data-testid="manage-labels-empty"
          >
            {t("nav.noLabels")} — tap to create one
          </button>
        ) : (
          labels.map((l) => {
            const active = activeLabel === l.name;
            const dropKey = `label:${l.name}`;
            const dropper = {
              onDragEnter: (e) => { if (noteIdFromEvent(e)) { e.preventDefault(); setDragOver(dropKey); } },
              onDragOver: (e) => { if (noteIdFromEvent(e)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOver !== dropKey) setDragOver(dropKey); } },
              onDragLeave: () => setDragOver((cur) => (cur === dropKey ? null : cur)),
              onDrop: (e) => {
                e.preventDefault();
                const id = noteIdFromEvent(e);
                setDragOver(null);
                if (id && onDropNote) onDropNote(id, "label", l.name);
              },
            };
            return (
              <button
                key={l.id}
                onClick={() => { setView(`label:${l.name}`); onClose?.(); }}
                data-testid={`sidebar-label-${l.name}`}
                {...dropper}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-2 rounded-full text-sm transition-colors",
                  "hover:bg-stone-100 dark:hover:bg-stone-800",
                  active && "bg-amber-100/80 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100",
                  dragClass(dropKey)
                )}
              >
                <Tag className={cn("h-3.5 w-3.5", labelStyle(labels, l.name).icon)} />
                <span className="truncate">{l.name}</span>
              </button>
            );
          })
        )}
      </nav>

      <div className="px-3 pt-2 pb-2 border-t border-border space-y-1.5 shrink-0">
        {authState === "user" && user ? (
          <div className="space-y-2">
          <TipSummaryCard />
          <div className="flex items-center gap-2 px-1 py-1.5 rounded-xl bg-stone-100/60 dark:bg-stone-800/60" data-testid="sidebar-user">
            {user.picture ? (
              <img src={user.picture} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />
            ) : (
              <div className="h-7 w-7 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-semibold">
                {(user.name || user.email || "?").trim().charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user.name || user.email}</div>
              <div className="text-[10px] text-stone-500 truncate">{user.email}</div>
            </div>
            <button
              onClick={signOut}
              className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-stone-200 dark:hover:bg-stone-700"
              title="Sign out"
              data-testid="sidebar-signout-btn"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Google OAuth was removed in favor of email + one-time code —
                the self-hosted OAuth client was returning "access blocked"
                until the user finishes the Google Cloud consent screen,
                and the email flow works without any 3rd-party setup. The
                SAME button is used to sign in to retrieve an existing
                backup — users just enter the same email they used before
                and the OTP flow signs them in. */}
            <button
              onClick={() => setEmailAuthOpen(true)}
              data-testid="sidebar-signin-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold hover:brightness-110 transition-all shadow-sm"
            >
              <Mail className="h-3.5 w-3.5" />
              Sign in / Back up
            </button>
            <p className="text-[10px] text-stone-500 dark:text-stone-400 px-1 leading-snug">
              Already backed up? Same email signs you in.
            </p>
          </div>
        )}
        <a
          href="/send"
          className="w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border text-xs font-medium hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          data-testid="sidebar-send-files-link"
        >
          <UploadCloud className="h-3.5 w-3.5" />
          Send big files
          {newDownloads > 0 && <span className="ml-auto rounded-full bg-emerald-600 text-white px-1.5 text-[10px] font-semibold" data-testid="sidebar-new-downloads">{newDownloads} new</span>}
        </a>
        <button
          onClick={() => setShareAppOpen(true)}
          className="w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-amber-300/70 dark:border-amber-700/60 bg-amber-50/60 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-xs font-medium hover:bg-amber-100 dark:hover:bg-amber-950/60 transition-colors"
          data-testid="share-app-btn"
        >
          <Send className="h-3.5 w-3.5" />
          Share with a friend
        </button>
        <ShareAppDialog open={shareAppOpen} onOpenChange={setShareAppOpen} />
        <button
          onClick={async () => {
            try {
              const n = await exportAllNotes();
              if (n === -1) return; // user cancelled the save picker — no toast
              if (n === 0) toast.error(t("export.empty"));
              else toast.success(`Exported ${n} note${n > 1 ? "s" : ""} with media`);
            } catch (err) {
              toast.error("Export failed — please try again");
              console.warn("[export] failed:", err);
            }
          }}
          className="w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-border text-xs font-medium hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          data-testid="export-all-btn"
        >
          <Download className="h-3.5 w-3.5" />
          {t("export.cta")}
        </button>
        {authState === "user" && (
          <button
            onClick={async () => {
              const first = window.confirm("This will permanently delete ALL your cloud notes and labels. Continue?");
              if (!first) return;
              const second = window.prompt("Type DELETE to confirm:");
              if (second !== "DELETE") { toast.message("Cancelled"); return; }
              try {
                await api.post("/user/wipe");
                toast.success("All your cloud data has been deleted");
                setTimeout(() => window.location.reload(), 800);
              } catch (err) {
                toast.error("Could not wipe data — try again");
                console.warn("[wipe] failed:", err);
              }
            }}
            className="w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-rose-300/60 dark:border-rose-700/60 text-rose-700 dark:text-rose-300 text-xs font-medium hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
            data-testid="wipe-data-btn"
          >
            <Eraser className="h-3.5 w-3.5" />
            Wipe cloud data
          </button>
        )}
        {authState === "user" && (
          <button
            onClick={async () => {
              // Hard delete the account: wipes notes, labels, shares, the
              // user row, and every session. Two confirmations because this
              // is irreversible — the user's email is gone from our DB.
              const first = window.confirm(
                "Delete your Mango Notes account?\n\n" +
                "This permanently removes your email, all your cloud notes, labels, and share links. This cannot be undone."
              );
              if (!first) return;
              const second = window.prompt("Type DELETE MY ACCOUNT to confirm:");
              if (second !== "DELETE MY ACCOUNT") { toast.message("Cancelled"); return; }
              try {
                await api.delete("/user/account");
                toast.success("Your account has been deleted");
                // Wipe local IndexedDB so the next visitor on this device
                // starts from a clean slate (not someone else's notes).
                try {
                  localStorage.clear();
                  const dbs = await (indexedDB.databases ? indexedDB.databases() : Promise.resolve([]));
                  dbs.forEach((d) => d.name && indexedDB.deleteDatabase(d.name));
                } catch {}
                setTimeout(() => { window.location.href = "/"; }, 800);
              } catch (err) {
                toast.error("Could not delete account — please try again");
                console.warn("[delete-account] failed:", err);
              }
            }}
            className="w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-rose-500/80 bg-rose-50/40 dark:bg-rose-950/40 text-rose-800 dark:text-rose-200 text-xs font-semibold hover:bg-rose-100 dark:hover:bg-rose-900/50 transition-colors"
            data-testid="delete-account-btn"
          >
            <UserX className="h-3.5 w-3.5" />
            Delete my account
          </button>
        )}
        {authState !== "user" && (
          <p className="text-[10px] text-stone-500 dark:text-stone-400 px-1 leading-snug italic">
            Guest mode — your notes live only on this device. Sign in to back up or to delete a cloud account.
          </p>
        )}
        <p className="text-[10px] text-stone-500 dark:text-stone-400 pt-1 leading-tight">
          <span className="font-semibold">Mango Notes</span> · {t("app.tagline")}
        </p>
      </div>
      <EmailAuthDialog open={emailAuthOpen} onOpenChange={setEmailAuthOpen} />
    </aside>
  );

  return (
    <>
      {/* Desktop — sticky so the sidebar stays pinned while the user
          scrolls through notes. The header is 4 rem (64 px) tall and
          already sticky at the top, so we offset the sidebar below it. */}
      <div className="hidden md:block sticky top-16 self-start h-[calc(100vh-4rem)]">{Content}</div>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="sidebar-mobile-overlay">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-background shadow-xl">
            {Content}
          </div>
        </div>
      )}
    </>
  );
}
